import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@ndk/ng-storage';
import { NDDTranslationService } from '@ndk/ng-translation';

import { AuthService } from '@core/authentication/auth.service';
import { KENDO_TOKEN } from '@core/kendo/kendo.value';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';

import { INDDTreeGridHeaderAction, NDDTreeGridColumn, NDDTreeGridSettings } from './shared/ndd-ng-tree-grid.model';

@Component({
    selector: 'ndd-ng-tree-grid',
    template: require('./ndd-ng-tree-grid.component.html'),
})

export class NDDTreeGridComponent implements OnDestroy, AfterViewInit {
    @Input() set treeGridSettings(settings: NDDTreeGridSettings) {
        this.settings = settings;
        this.initializeKendoTreeList();
    }
    @Input() public treeGridDisabled?: boolean;
    @Output() public onTreeGridSelectionChange: EventEmitter<object> = new EventEmitter();
    public treeList: any;
    public settings: NDDTreeGridSettings;
    public filterText: string = '';
    public filtered: boolean = false;
    public isLoading: boolean = true;
    private readonly MOBILE_WIDTH: number = 768;
    @ViewChild('treelist', { static: true }) private treelistElement: ElementRef;
    private selectedItem: object;
    /* Propriedade utilizada para saber se deve ocultar o loading */
    private setLoadingToFalse: boolean = false;
    private readonly KEYCODE_ENTER: number = 13;

    private readonly HTTP_STATUS_UNAUTHORIZED: number = 401;
    private readonly HTTP_STATUS_FORBIDDEN: number = 403;
    private readonly HTTP_STATUS_NOTFOUND: number = 404;

    constructor(
        @Inject(KENDO_TOKEN) private kendo: any,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private translationService: NDDTranslationService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
    ) { }

    public ngOnDestroy(): void {
        this.kendo.destroy(this.treelistElement);
    }
    public ngAfterViewInit(): void {
        this.isLoading = true;
        this.adjustTreeGridHeight();
        this.cdr.detectChanges();
    }
    /** recalcula a altura do grid. */
    public adjustTreeGridHeight(): void {
        const offsetHeight: number = 38;
        const parentDiv: any = this.getParentDiv(this.treelistElement.nativeElement.parentNode);
        const hostHeight: any = parentDiv.offsetHeight;
        this.kendo.jQuery(this.treelistElement.nativeElement).height(hostHeight - offsetHeight);
        this.treeList.refresh();
    }

    /** executado ao fazer o resize da tela. */
    public onResize(event: any): void {
        this.adjustTreeGridHeight();
    }
    /** Recarrega os dados */
    public refresh(): void {
        this.isLoading = true;
        this.clearSelection();
        this.treeList.dataSource.read();
    }

    /** Método utilizado para disparar o filtro do kendo. Valida o texto digitado e cria filters utilizados pelo kendo*/
    public applyFilter(): void {
        if (!this.settings.filterOptions) { return; }
        if (!this.filterText) { return; }
        this.filterText = this.filterText.trim();
        if (this.filterText.length === 0) { return; }

        this.isLoading = true;

        this.clearSelection();

        const filter: any[] = [];
        this.filtered = true;

        this.settings.filterOptions.fields.forEach((field: string) => {
            const newFilterItem: any = { field, operator: 'contains', value: `'${ this.filterText }'` };
            filter.push(newFilterItem);
        });

        this.treeList.dataSource.filter(filter);
    }

    /** Limpa o campo de filtro e atualiza os filtros no kendo */
    public clearFilter(): void {
        if (!this.settings.filterOptions) { return; }

        this.isLoading = true;
        this.clearSelection();
        this.filtered = false;
        this.filterText = '';
        this.treeList.dataSource.filter([]);
    }

    /** Executado quando o campo de pesquisa é alterado */
    public onFilterChange(): void {
        this.filtered = false;
    }

    /** Executado quando é pressionada alguma tecla no campo de pesquisa. Verifica se o usuário pressionou ENTER. */
    public onFilterKeyUp(event: any): void {
        if (event.keyCode === this.KEYCODE_ENTER) {
            this.filterText = this.filterText.trim();
            if (this.filterText.length > 0) {
                this.applyFilter();
            } else {
                this.clearFilter();
            }
        } else {
            this.filtered = false;
        }
    }

    /** Disparado toda vez que o item selecionado é alterado (Kendo) */
    private onSelectedItemChange(item: any): void {
        this.selectedItem = item.sender.dataItem(item.sender.select());
        this.refreshActionsAttrDisabled();
        this.onTreeGridSelectionChange.emit(this.selectedItem);
    }

    /** Inicializa o Kendo TreeList. Seta o datasource, schema etc.. */
    private initializeKendoTreeList(): void {
        this.isLoading = true;
        const kendoDataSource: any = {
            serverFiltering: true,
            transport: {
                type: 'odata',
                read: {
                    beforeSend: (xhr: any): void => {
                        xhr.setRequestHeader('Authorization', 'Bearer ' + this.settings.dataSource.readToken);
                    },
                    url: this.settings.dataSource.readUrl,
                    dataType: 'json',
                },
                parameterMap: (data: any, type: any): object => this.parameterMap(data, type),
            },
            schema: {
                data: 'value',
                model: {
                    data: 'value',
                    hasChildren: true,
                },
                expanded: true,
            },
            sort: ({ field: this.settings.sortBy?.field, dir: this.settings.sortBy?.dir }),
            error: (error: any): void => this.onError(error),
        };
        this.settings.columns.map((col: NDDTreeGridColumn) => {
            if (!col.template) {
                col.template = '';
            }
            if (col.hideWhenResponsive) {
                col.minScreenWidth = this.MOBILE_WIDTH;
            }
        });
        const kendoTreelist: any = this.kendo.jQuery(this.treelistElement.nativeElement);

        kendoTreelist.kendoTreeList({
            dataSource: kendoDataSource,
            resizable: this.settings.isResizable || null,
            dataBinding: (e: any): void => this.onDataBind(e),
            sortable: !this.settings.sortBy ? false : {
                mode: 'single',
                allowUnsort: false,
            },
            scrollable: true,
            selectable: true,
            columns: this.settings.columns,
            change: (item: any): void => {
                this.onSelectedItemChange(item);
            },
            messages: { noRows: this.translationService.instant('360KendoGridNoRecords') },
        });
        this.treeList = kendoTreelist.data().kendoTreeList;
        this.refreshActionsAttrDisabled();
    }

    /** Evento disparado quando os dados do kendo são atualizados. Utilizado para expandir os nós após a pesquisa */
    private onDataBind(e: any): void {
        /* Remove o loading somente caso não seja a primeira vez que chama esse método.
         O Kendo dispara esse método duas vezes, na inicialização e ao receber os dados, então
         remove o loading apenas depois de receber os dados */
        if (this.setLoadingToFalse) {
            this.isLoading = false;
        }
        this.setLoadingToFalse = true;
        const data: any[] = e.sender.dataSource.data();
        if (this.settings.defaultParentItem && !data.some((x: any) => x.id === 0)) {
            /* adiciona o parent item default */
            this.addDefaultParentItem(data);
        }

        if (!this.filtered) { return; }
        /* Expande todos os itens já carregados */
        this.expandAllLoadedNodes();
    }

    /** Adiciona o defaultParentItem na lista utilizada pelo treelist */
    private addDefaultParentItem(data: any[]): void {
        const defaultParent: any = this.settings.defaultParentItem;
        defaultParent.id = 0;
        defaultParent.parentId = null;
        data.map((item: any) => {
            if (!item.parentId) {
                item.parentId = 0;
            }
        });
        data.unshift(defaultParent);
        this.treeList.dataSource.data(data);
        /* expande o nó que foi adicionado como parent default */
        this.treeList.dataSource.data().filter((node: any) => node.id === 0).forEach((node: any) => {
            node.loaded(true);
            this.treeList.expand(node);
        });
    }

    /** Expande todos os nós, já carregados, do treelist. Utilizado ao pesquisar. */
    private expandAllLoadedNodes(): void {
        this.treeList.dataItems().forEach((item: any) => {
            if (item.loaded()) {
                this.treeList.expand(item);
            }
        });
    }

    /** Utilizado para adicionar os parametros de filtro e carregamento ondemand do kendo. */
    private parameterMap(data: any, type: string): object {
        if (type !== 'read') { return; }
        /* filtro default, para trazer todos os nós da raiz */
        let filter: any = { $filter: 'parentId eq null' };
        if (data.id) { /* quando expande um item */
            /* gera o filtro odata para carregar sobre demanda */
            filter.$filter = `parentId eq ${ data.id }`;
        } else if (data.filter) {/* quando é pesquisado */
            filter = {};
            /* gera o filtro odata de acordo com FilterOptions */
            data.filter.filters.forEach((filterItem: any) => {
                if (filterItem.value) {
                    filter[filterItem.field] = filterItem.value;
                }
            });

        }

        return filter;
    }

    /** Limpa o item selecionado */
    private clearSelection(): void {
        this.treeList.clearSelection();
        this.onTreeGridSelectionChange.emit(undefined);
    }

    /** Atualiza as ações que devem estar desabilitadas. (que dependem do item selecionado) */
    private refreshActionsAttrDisabled(): void {
        if (!this.settings.actions) { return; }

        this.settings.actions
            .map((action: INDDTreeGridHeaderAction) => {
                if (action.needSingleGridRowSelected) {
                    action.disabled = !this.selectedItem;
                }

                if (action.children) {
                    action.children.map((actionChildren: INDDTreeGridHeaderAction) => {
                        if (actionChildren.needSingleGridRowSelected) {
                            actionChildren.disabled = !this.selectedItem;
                        }
                    });
                }
            });
    }

    private getParentDiv(node: any): any {
        if (node.nodeName.toLowerCase() === 'div' || node.nodeName.toLowerCase() === 'body') {
            return node;
        }

        return this.getParentDiv(node.parentNode);
    }

    private onError(error: any): void {
        switch (error.xhr.status) {
            case this.HTTP_STATUS_UNAUTHORIZED:
                this.handleUnauthorizedRequest();
                break;

            case this.HTTP_STATUS_FORBIDDEN:
                this.handleForbiddenRequest();
                break;

            case this.HTTP_STATUS_NOTFOUND:
                this.handleNotFoundRequest();
                break;

            default:
                break;
        }
    }

    private handleUnauthorizedRequest(): void {
        this.authService.loggedIn = false;

        try {
            this.localStorageService.deleteValue(LocalStorageKeys.Token);
            this.localStorageService.deleteValue(LocalStorageKeys.AccountType);
        } finally {
            this.authService.navigateToLogin();
        }
    }

    private handleForbiddenRequest(): void {
        this.router.navigate(['page-forbidden']);
    }

    private handleNotFoundRequest(): void {
        this.router.navigate(['page-not-found']);
    }
}
